<template>
  <div :class="[$style.base, { [$style.smallScreen]: $screen !== 'l' }]">
    <BaseCard
      :heading="filters.capitalize($t('global.preview'))"
      fullHeight
    >
      <div
        ref="wrapperEl"
        :class="$style.iframeWrapper"
        :style="wrapperStyle"
      >
        <iframe
          ref="iframeEl"
          :src="`${config.backendUrl}/orders/${orderId}/preview`"
          :class="$style.iframe"
          :style="iframeScaleStyle"
          v-test="'issue-invoice-preview-iframe'"
        />
      </div>
      <BaseSpinner v-if="!isLoaded" />
    </BaseCard>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useElementSize } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import config from '@/config';
import filters from '@/filters';

const props = defineProps<{
  orderId: string | number;
  isLoaded: boolean;
}>();

const emit = defineEmits(['update:isLoaded']);

const NATURAL_WIDTH = 794; // A4 width in pixels

const wrapperEl = ref<HTMLDivElement | null>(null);
const { width: containerWidth } = useElementSize(wrapperEl);
const iframeEl = ref<HTMLIFrameElement | null>(null);
const iframeNaturalHeight = ref(0);

const scale = computed(() =>
  containerWidth.value > 0
    ? Math.min(1, containerWidth.value / NATURAL_WIDTH)
    : 1,
);

const iframeScaleStyle = computed(() => ({
  width: `${NATURAL_WIDTH}px`,
  transformOrigin: 'top left',
  transform: `scale(${scale.value})`,
}));

const wrapperStyle = computed(() =>
  iframeNaturalHeight.value
    ? { height: `${(iframeNaturalHeight.value + 2) * scale.value}px` }
    : {},
);

const setHeight = (event: MessageEvent) => {
  if (event.source !== iframeEl.value?.contentWindow) {
    return;
  }

  if (event.data?.iframeHeight && iframeEl.value) {
    const padding = 16 * 6;
    const naturalHeight = event.data.iframeHeight + padding;
    iframeNaturalHeight.value = naturalHeight;
    iframeEl.value.style.height = `${naturalHeight}px`;
  }
};

const handleMessage = (event: MessageEvent, loaded: boolean) => {
  setHeight(event);
  emit('update:isLoaded', loaded);
};

onMounted(() =>
  window.addEventListener('message', (event: MessageEvent) => {
    handleMessage(event, true);
  }),
);
onUnmounted(() =>
  window.removeEventListener('message', (event: MessageEvent) => {
    handleMessage(event, false);
  }),
);
</script>

<style
  lang="scss"
  module
>
.base {
  &.smallScreen {
    padding-bottom: 0;
  }
  &:not(.smallScreen) {
    padding-bottom: $spacing * 2;
  }
}
.iframeWrapper {
  width: 100%;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid var(--color-border);

  .base:not(.smallScreen) & {
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }
}
.iframe {
  padding: $spacing * 2;
  margin: 0 auto;
  border: none;
}
</style>
